import React from 'react';
import { Instagram, Globe, Palette } from 'lucide-react';
import { Input } from '../../ui/input';
import { OnboardingFormData } from '../../../types/business';
import { cn } from '../../../lib/utils';

interface StepSocialProps {
  formData: OnboardingFormData;
  updateField: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
  error: string | null;
}

const BRAND_COLORS = [
  '#C084FC', // Fash purple (default)
  '#F472B6', // Pink
  '#FB923C', // Orange
  '#FBBF24', // Yellow
  '#34D399', // Green
  '#60A5FA', // Blue
  '#818CF8', // Indigo
  '#E879F9', // Fuchsia
  '#1F2937', // Dark
  '#EF4444', // Red
];

export function StepSocial({ formData, updateField, error }: StepSocialProps) {
  const updateSocial = (field: string, value: string) => {
    updateField('socialMedia', { ...formData.socialMedia, [field]: value });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif font-semibold text-heading">
          Social & Branding
        </h2>
        <p className="text-body mt-1">
          Connect your online presence and personalize your boutique page. All fields are optional.
        </p>
      </div>

      {/* Social Media Links */}
      <div className="space-y-4">
        <label className="text-sm font-medium text-heading block">Social Media</label>

        {/* Instagram */}
        <div className="space-y-1.5">
          <label className="text-xs text-body">Instagram</label>
          <div className="relative">
            <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="@yourboutique"
              value={formData.socialMedia.instagram}
              onChange={(e) => updateSocial('instagram', e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* TikTok */}
        <div className="space-y-1.5">
          <label className="text-xs text-body">TikTok</label>
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.72a8.2 8.2 0 004.76 1.52V6.8a4.85 4.85 0 01-1-.11z" />
            </svg>
            <Input
              type="text"
              placeholder="@yourboutique"
              value={formData.socialMedia.tiktok}
              onChange={(e) => updateSocial('tiktok', e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Website */}
        <div className="space-y-1.5">
          <label className="text-xs text-body">Website</label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="url"
              placeholder="https://yourboutique.com"
              value={formData.socialMedia.website}
              onChange={(e) => updateSocial('website', e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Brand Color */}
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-heading flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Brand Color
          </label>
          <p className="text-xs text-body mt-0.5">
            Accent color used on your boutique page
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {BRAND_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => updateField('brandColor', color)}
              className={cn(
                'w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center',
                formData.brandColor === color
                  ? 'border-heading scale-110 shadow-md'
                  : 'border-transparent hover:scale-105'
              )}
              style={{ backgroundColor: color }}
            >
              {formData.brandColor === color && (
                <div className="w-3 h-3 bg-white rounded-full shadow-sm" />
              )}
            </button>
          ))}
        </div>

        {/* Custom color input */}
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full border-2 border-gray-200 flex-shrink-0"
            style={{ backgroundColor: formData.brandColor }}
          />
          <Input
            type="text"
            placeholder="#C084FC"
            value={formData.brandColor}
            onChange={(e) => {
              const value = e.target.value;
              if (value.match(/^#[0-9A-Fa-f]{0,6}$/)) {
                updateField('brandColor', value);
              }
            }}
            maxLength={7}
            className="w-32 font-mono"
          />
        </div>
      </div>

      {/* Preview */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-heading">Preview</label>
        <div className="rounded-card border border-gray-100 overflow-hidden">
          <div
            className="h-20 w-full"
            style={{ backgroundColor: formData.brandColor + '20' }}
          />
          <div className="p-4 -mt-6">
            <div className="flex items-end gap-3">
              {formData.logoPreview ? (
                <img
                  src={formData.logoPreview}
                  alt="Logo"
                  className="w-12 h-12 rounded-full border-2 border-white object-cover shadow-sm"
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-white font-serif text-lg"
                  style={{ backgroundColor: formData.brandColor }}
                >
                  {formData.name?.[0]?.toUpperCase() || 'B'}
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-heading">
                  {formData.name || 'Your Boutique'}
                </p>
                <p className="text-xs text-body">
                  {formData.categories.slice(0, 2).join(' · ') || 'Fashion'}
                </p>
              </div>
            </div>
            {(formData.socialMedia.instagram || formData.socialMedia.website) && (
              <div className="flex gap-3 mt-3 text-xs text-body">
                {formData.socialMedia.instagram && (
                  <span>{formData.socialMedia.instagram}</span>
                )}
                {formData.socialMedia.website && (
                  <span className="truncate">{formData.socialMedia.website}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
